import showToast from '@jpi-cloud-web-pro/jpi-cloud-shared/components/Notification';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { CLOUD_STATUS_CHECK_INTERVAL } from '@/config/common';

import events, { eventEmitter } from '@/events';

import { preventGoogleFontsLoading } from './utils';

import { useSelectedSystem } from '@/pages/SystemSettings/hooks/useSelectedSystem';
import { getLastUsedSystemId, removeLastUsedSystemId } from '@/utils/localStorage/lastUsedSystem';
import logger from '@/utils/logger';
import { UserDataState } from './types';

export function useAppInitializer({
  userLoggedIn,
  userDataState,
  userInfo,
  servicePartner,
  initApp,
  getCloudStatus,
  getUserInfo,
  getSPSubscriptionStatus,
  getActiveProAccountsIds,
  getCountries,
  getLatestAgreementsVersions,
  getAllBrands,
  setUserDataState,
  getProAccount,
  getAllLanguages,
  getSupportedLanguages,
  selectSystem,
}) {
  const refreshIntervalRef = useRef(null);
  const hasInitialized = useRef(false);
  const hasUserInfo = Boolean(userInfo);
  const isAppFullyLoaded = userDataState === UserDataState.LOADED && userLoggedIn;

  const lastUsedSystemId = useMemo(() => {
    if (isAppFullyLoaded) {
      return getLastUsedSystemId();
    }
    return null;
  }, [isAppFullyLoaded]);
  const clearForbiddenLastUsedSystem = useCallback(() => {
    if (getLastUsedSystemId() === lastUsedSystemId) {
      removeLastUsedSystemId();
    }
  }, [lastUsedSystemId]);
  const { selectedSystem: lastUsedSystem } = useSelectedSystem(lastUsedSystemId, {
    onError: clearForbiddenLastUsedSystem,
  });

  useEffect(() => {
    if (lastUsedSystem) {
      selectSystem(lastUsedSystem);
    }
  }, [lastUsedSystem, selectSystem]);

  const showErrorToast = useCallback(() => {
    showToast(
      'error',
      <FormattedMessage id="generic.error.request.unknown" defaultMessage="An error has occurred. Try again later." />,
    );
  }, []);

  const showTokenExpiredToast = useCallback(() => {
    if (userLoggedIn) {
      showToast(
        'warning',
        <FormattedMessage id="warning.auth.token" defaultMessage="Your session has expired, please log in again." />,
      );
    }
  }, [userLoggedIn]);

  const checkCloudStatus = useCallback(async () => {
    try {
      await getCloudStatus();
    } catch (error) {
      eventEmitter.emit(events.appInsights.logError, error);
      showErrorToast();
    }
  }, [getCloudStatus, showErrorToast]);

  const startCloudPolling = useCallback(() => {
    if (refreshIntervalRef.current == null) {
      refreshIntervalRef.current = setInterval(checkCloudStatus, CLOUD_STATUS_CHECK_INTERVAL);
    }
  }, [checkCloudStatus]);

  const stopCloudPolling = useCallback(() => {
    if (refreshIntervalRef.current != null) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  const initializeApp = useCallback(async () => {
    try {
      await Promise.all([initApp(), getCountries(), getLatestAgreementsVersions(), getCloudStatus()]);

      startCloudPolling();
    } catch (error) {
      eventEmitter.emit(events.appInsights.logError, error);
      showErrorToast();
    }
  }, [initApp, getCountries, getLatestAgreementsVersions, getCloudStatus, startCloudPolling, showErrorToast]);

  const loadUserData = useCallback(async () => {
    try {
      await Promise.all([getAllLanguages(), getProAccount(userInfo.spId), getSPSubscriptionStatus()]);
    } catch (error) {
      showErrorToast();
      logger.toAppInsights?.(error);
    }
  }, [getAllLanguages, getProAccount, userInfo?.spId, getSPSubscriptionStatus, showErrorToast]);

  const manageUserDataStates = useCallback(async () => {
    try {
      if (userDataState === UserDataState.REQUESTED && !hasUserInfo && userLoggedIn) {
        await getUserInfo();
        return;
      }

      if (userDataState === UserDataState.REQUESTED && hasUserInfo) {
        if (!servicePartner) {
          await getUserInfo();
        }

        if (!servicePartner && userInfo?.spId) {
          await loadUserData();
        }

        const loadLanguage = servicePartner ? getSupportedLanguages : getAllLanguages;
        await Promise.all([loadLanguage(), getAllBrands()]);
        setUserDataState(UserDataState.LOADING);
        return;
      }

      if (userDataState === UserDataState.LOADING && hasUserInfo) {
        setUserDataState(UserDataState.LOADED);
      }
    } catch (error) {
      showErrorToast();
      logger.toAppInsights?.(error);
    }
  }, [
    userDataState,
    userLoggedIn,
    hasUserInfo,
    servicePartner,
    userInfo?.spId,
    getUserInfo,
    loadUserData,
    getSupportedLanguages,
    getAllLanguages,
    getAllBrands,
    setUserDataState,
    showErrorToast,
  ]);

  useEffect(() => {
    preventGoogleFontsLoading();

    if (!hasUserInfo && !hasInitialized.current) {
      hasInitialized.current = true;
      initializeApp();
    }

    eventEmitter.on(events.inApp.auth.TOKEN_EXPIRED, showTokenExpiredToast);

    return () => {
      eventEmitter.off(events.inApp.auth.TOKEN_EXPIRED, showTokenExpiredToast);
      stopCloudPolling();
    };
  }, [hasUserInfo, initializeApp, showTokenExpiredToast, stopCloudPolling]);

  useEffect(() => {
    manageUserDataStates();
  }, [manageUserDataStates]);

  useEffect(() => {
    if (userLoggedIn) {
      setUserDataState(UserDataState.REQUESTED);
    } else {
      setUserDataState(UserDataState.LOADED);
    }
  }, [userLoggedIn, setUserDataState]);

  useEffect(() => {
    if (userLoggedIn) {
      getActiveProAccountsIds();
    }
  }, [userLoggedIn, getActiveProAccountsIds]);

  const appLoading = userDataState !== UserDataState.LOADED;

  return { appLoading };
}
